import { Story } from '../../types/story';

export const romeCooking: Story = {
  id: 'rome-cooking',
  title: 'La Dolce Vita',
  excerpt: 'An American food blogger and an Italian chef discover that the best recipes aren\'t written down—they\'re created together, in a tiny Roman kitchen where the only ingredient that really matters is desire.',
  city: 'Rome',
  publishDate: '2026-01-28',
  readTime: '25 min read',
  tags: ['♂♀', 'cooking', 'food', 'cheerful', 'romantic'],
  categories: ['romantic'],
  intensity: 2,
  content: `I came to Rome for the food. That's the honest truth. I was a food blogger with a modest following, a cookbook that had done okay, and a dream of learning to cook like an Italian grandmother.

What I found was something much better.

"Scusi," I said, standing in the doorway of Trattoria da Mario, a tiny place in Trastevere that smelled like garlic and olive oil and happiness. "I'm looking for Mario."

The woman wiping down tables looked me up and down. "Mario is my husband. He is in the kitchen. What do you want?"

"I'm Emily," I said, trying to sound more confident than I felt. "I'm a food writer. I was hoping to learn some authentic Roman recipes."

She laughed, and it was like music. "Everyone wants to learn. Mario does not teach. Mario cooks."

"Please?" I tried. "I'll pay. I'll work for free. I just... I want to learn."

She studied me for a moment, then shrugged. "Come back tomorrow. Early. Before the lunch rush. We will see."

The next morning, I was there at 8 AM sharp. The trattoria was quiet, the morning light streaming through the windows, and Mario was indeed in the kitchen—a small, fiery man with flour on his apron and a scowl that suggested he'd been arguing with the pasta all morning.

"This is the American," his wife announced. "The one who wants to learn."

Mario looked me over, unimpressed. "Americans. You think you know food. You think because you put basil on something it is Italian."

"I want to learn the real way," I said. "The way your grandmother taught you."

He snorted. "My grandmother would turn over in her grave if she saw what I cook for tourists."

"Then cook for me," I challenged. "Not for tourists. For me. Show me what real Roman food is."

Something in my tone must have reached him, because he nodded once, sharp. "Fine. But you work. You peel potatoes, you chop onions, you clean fish. No writing, no taking pictures. Just work."

For two weeks, I did exactly that. I peeled mountains of potatoes, chopped endless onions, cleaned more fish than I knew existed. My hands were constantly raw, my back ached, and I smelled like garlic 24/7.

And I loved every minute of it.

Mario was a demanding teacher, but a brilliant one. He showed me how to make carbonara the right way—with guanciale, not pancetta, with egg yolks and pecorino, never cream. He taught me the secret to perfect cacio e pepe (the water, always the starchy pasta water). He showed me how to make amatriciana so spicy it made your eyes water but so delicious you couldn't stop eating.

His wife, Sofia, was the warmth to his fire. She'd bring us coffee during breaks, correct his Italian when he got too technical, taste everything we made and offer suggestions that were always spot-on.

"You're good," Mario said one day, watching me knead dough for pizza bianca. "Not bad for an American."

"I'm trying," I said, my arms aching from the effort.

"No," he said, shaking his head. "Not trying. Doing. You have the feel. You understand the food."

That was the highest compliment I could have received.

The day everything changed was a Tuesday. It was Sofia's birthday, and Mario wanted to make something special. Something just for family.

"We make supplì," he announced. "Roman street food. But my way."

Supplì were rice balls stuffed with mozzarella, breaded and fried. Simple, but Mario's version was anything but.

"You stuff with mozzarella," he instructed, "but also with prosciutto, with peas, with a little bit of the ragù from yesterday. Always use yesterday's food. Never waste."

We worked side by side in the small kitchen, our hands brushing occasionally, the air thick with the smell of frying oil and melting cheese. Sofia sat at the small table, drinking wine and offering commentary.

"More ragù," she'd say. "Less prosciutto. Mario, you always use too much prosciutto."

"It is my birthday present to you," he'd grumble, but he'd always adjust the seasoning.

When the supplì were ready, golden and crispy, we sat down together at the small table. Mario poured wine, Sofia lit candles, and for a moment, we weren't chef and student, husband and wife and American girl.

We were just family.

"These are perfect," I said after my first bite. The outside was crispy, the inside creamy, the flavors complex and beautiful.

"See?" Mario said to Sofia. "She understands."

"She has good taste," Sofia agreed. "Unlike some people I could mention."

We ate and drank and laughed for hours. The restaurant was closed, the city quiet outside, and in that tiny kitchen, I felt more at home than I had in years.

"You should stay," Sofia said suddenly. "In Rome. With us."

I froze, my fork halfway to my mouth. "What?"

"We have a room upstairs," she continued. "Small, but clean. You could work here, learn more. We could use the help."

"Sofia," Mario started, but she cut him off.

"Don't Sofia me," she said. "You like her. I like her. She's good for the restaurant. She's good for us."

Mario looked at me, and for once, his expression wasn't grumpy or demanding. It was... soft. "It would be good to have someone who understands food in the house," he admitted.

"I... I don't know what to say," I stammered.

"Say yes," Sofia said. "Say you'll stay."

And just like that, I did.

Living above the trattoria was like living in a dream. I'd wake up to the smell of garlic and coffee, spend my days learning from Mario, my nights writing about everything I was learning. Sofia treated me like a daughter, Mario like a protégé.

The three of us fell into an easy rhythm—cooking, eating, talking, laughing. We explored Rome together, visited markets, tried other restaurants, always coming back to our tiny kitchen, our little family.

The attraction between Mario and me was subtle at first. A look that lasted too long, a hand that brushed against mine, the way he'd stand just a little too close when showing me a technique.

Sofia noticed, of course. Sofia noticed everything.

"You should kiss her," she said one night, while Mario was out getting more wine. "He wants to. You want to. What are you waiting for?"

"He's married," I whispered, though it felt like a weak excuse.

"I'm not blind," she said simply. "And I'm not selfish. Mario has been lonely since our son moved to Milan. You make him happy again. You make us both happy."

"I couldn't—"

"You could," she insisted. "And you should."

That night, after the restaurant was closed, after the dishes were done, after the wine was flowing, Mario cornered me in the kitchen.

"Sofia says I should kiss you," he said, his voice low.

"What do you think?" I asked, my heart pounding.

"I think Sofia is usually right about these things," he said, and then he kissed me.

It was gentle at first, tentative, then deeper, more confident. He tasted like wine and garlic and something uniquely Mario. When we pulled apart, we were both breathing hard.

"This is complicated," I said.

"Everything worth having is complicated," he replied.

Sofia walked in then, saw us, and smiled. "About time," she said, and poured us all more wine.

That night changed everything. The three of us became something more than family, something more than friends. We became lovers, all three of us, in a way that felt natural, right, like we'd been heading toward this moment all along.

Our nights were spent in the big bed upstairs, tangled together, exploring each other with the same passion and attention we gave to our cooking. Mario was intense and demanding, Sofia was gentle and generous, and together they showed me what it meant to be loved completely.

Our days were spent in the kitchen, creating new dishes, experimenting with flavors, falling more in love with each other and with the food we made together.

"We should write a cookbook," Sofia said one morning, while we were making fresh pasta. "The three of us. Our recipes, our story."

"Who would publish it?" I asked. "A cookbook by a married couple and their American lover?"

"Someone smart," Mario said. "Someone who understands that love comes in many forms."

And he was right. We found a publisher, a small independent press that loved our concept. We spent the next year writing, testing recipes, taking photos, falling more in love with each other and with the life we were building together.

The book—called "La Famiglia" (The Family)—became a surprise hit. People loved the recipes, but they loved the story more. Three people, three generations, finding love and family in a tiny Roman kitchen.

We did a book tour—New York, London, Paris—but we always came back to Rome. Always came back to our tiny trattoria, our small apartment, our big bed.

Six months after the book came out, Mario proposed. Not just to me, but to Sofia too. A renewal of their vows, but with me included now.

We had a small ceremony in the garden behind the restaurant. Just the three of us, a few close friends, and the priest who had married Mario and Sofia thirty years earlier.

"I now pronounce you family," he said, and we all cried.

Life wasn't always easy. Three people in one relationship came with its own complications. Jealousy sometimes crept in, disagreements about everything from recipes to finances, the challenge of explaining our relationship to the outside world.

But we always came back to the kitchen. Always came back to cooking together, to feeding each other, to the simple act of creating something beautiful and delicious and sharing it.

That's what love is, I think. Not grand gestures or dramatic declarations. It's the small, daily acts of care. The way Mario still wakes up early to make coffee. The way Sofia still tastes everything before it goes out to customers. The way we all still gather in the kitchen every night, cooking, eating, loving.

This is my life now. In Rome, in a tiny apartment above a trattoria, with two people who love me completely. I write about food, I cook with my family, I live la dolce vita.

And it's better than any recipe I could have ever imagined.`
};
